/**
 * Maps Firebase error codes to user-friendly messages
 */
const FIREBASE_ERROR_MAP = {
  // Auth errors
  'auth/user-not-found': 'errors.auth_user_not_found',
  'auth/wrong-password': 'errors.auth_wrong_password',
  'auth/email-already-in-use': 'errors.auth_email_in_use',
  'auth/weak-password': 'errors.auth_weak_password',
  'auth/invalid-email': 'errors.auth_invalid_email',
  'auth/too-many-requests': 'errors.auth_too_many_requests',
  'auth/invalid-credential': 'errors.auth_invalid_credential',
  'auth/user-disabled': 'errors.auth_user_disabled',
  'auth/operation-not-allowed': 'errors.auth_operation_not_allowed',

  // Firestore errors
  'permission-denied': 'errors.permission_denied',
  'unavailable': 'errors.network_error',
  'not-found': 'errors.not_found',
  'already-exists': 'errors.already_exists',
  'failed-precondition': 'errors.failed_precondition',
  'resource-exhausted': 'errors.resource_exhausted',
  'cancelled': 'errors.cancelled',
  'deadline-exceeded': 'errors.deadline_exceeded',
};

/**
 * Parses a Firebase error and returns a translation key
 * @param {Error} error - The Firebase error object
 * @returns {string} - Translation key for the error message
 */
export function parseFirebaseError(error) {
  if (!error) {
    return 'errors.default';
  }

  // Handle Firebase Auth errors (error.code format: 'auth/error-code')
  if (error.code && FIREBASE_ERROR_MAP[error.code]) {
    return FIREBASE_ERROR_MAP[error.code];
  }

  // Handle Firestore errors (error.code format: 'permission-denied')
  if (error.code && FIREBASE_ERROR_MAP[error.code]) {
    return FIREBASE_ERROR_MAP[error.code];
  }

  // Check if it's a network error
  if (error.message?.includes('network') || error.message?.includes('Network')) {
    return 'errors.network_error';
  }

  // Check for permission denied in message
  if (error.message?.includes('permission') || error.message?.includes('Permission')) {
    return 'errors.permission_denied';
  }

  // Default error
  return 'errors.default';
}

/**
 * Gets a user-friendly error message from a Firebase error
 * Uses i18n translation function if provided, otherwise returns fallback
 * @param {Error} error - The Firebase error object
 * @param {Function} t - Optional i18n translation function
 * @returns {string} - User-friendly error message
 */
export function getErrorMessage(error, t) {
  const key = parseFirebaseError(error);

  if (t && typeof t === 'function') {
    return t(key);
  }

  // Fallback messages when translation is not available
  const FALLBACK_MESSAGES = {
    'errors.auth_user_not_found': 'No account found with this email.',
    'errors.auth_wrong_password': 'Incorrect password.',
    'errors.auth_email_in_use': 'Email already registered.',
    'errors.auth_weak_password': 'Password is too weak.',
    'errors.auth_invalid_email': 'Invalid email address.',
    'errors.auth_too_many_requests': 'Too many attempts. Please try again later.',
    'errors.auth_invalid_credential': 'Invalid email or password.',
    'errors.auth_user_disabled': 'This account has been disabled.',
    'errors.auth_operation_not_allowed': 'This operation is not allowed.',
    'errors.permission_denied': 'You do not have permission to perform this action.',
    'errors.network_error': 'Connection error. Please check your internet.',
    'errors.not_found': 'The requested resource was not found.',
    'errors.already_exists': 'This resource already exists.',
    'errors.failed_precondition': 'Operation failed. Please try again.',
    'errors.resource_exhausted': 'Too many requests. Please try again later.',
    'errors.cancelled': 'Operation was cancelled.',
    'errors.deadline_exceeded': 'Request timed out. Please try again.',
    'errors.default': 'Something went wrong. Please try again.',
  };

  return FALLBACK_MESSAGES[key] || FALLBACK_MESSAGES['errors.default'];
}

const errorHandler = {
  parseFirebaseError,
  getErrorMessage,
};

export default errorHandler;
